// src/components/chat/CopyButton.tsx - Enhanced version for Office Add-ins
import * as React from "react";
import { createLogger } from "../../utils/logger";
import { convertMarkdownToHtml } from "../../utils/markdownToHtml";
import { stripMetadataSections } from "../../utils/contentFilters";

const log = createLogger("CopyButton");

interface CopyButtonProps {
  textToCopy: string;
  className?: string;
}

/**
 * Office Add-in optimized copy button that works reliably across different environments
 * Uses a robust fallback chain specifically designed for sandboxed iframe contexts
 */
const CopyButton: React.FC<CopyButtonProps> = ({ textToCopy, className = "" }) => {
  // Track copy state for user feedback
  const [copyState, setCopyState] = React.useState<"idle" | "success" | "error">("idle");
  const [isHovered, setIsHovered] = React.useState(false);

  // Auto-reset copy state after showing feedback
  React.useEffect(() => {
    if (copyState !== "idle") {
      const timer = setTimeout(() => setCopyState("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [copyState]);

  /**
   * Primary copy method with HTML format support using event listener pattern
   * This method provides both text/html and text/plain formats to the clipboard
   * Word Desktop uses HTML format for rich text, while plain text serves as fallback
   */
  const copyWithHtmlFormat = async (text: string): Promise<boolean> => {
    try {
      // Convert markdown to HTML
      const htmlContent = await convertMarkdownToHtml(text);

      // Create event listener that sets both HTML and plain text formats
      let copySuccess = false;

      const copyListener = (e: ClipboardEvent) => {
        if (e.clipboardData) {
          // Set HTML format for Word Desktop (preserves rich formatting)
          e.clipboardData.setData("text/html", htmlContent);
          // Set plain text format as fallback for compatibility
          e.clipboardData.setData("text/plain", text);
          e.preventDefault();
          copySuccess = true;

          log.info("Set clipboard data with both formats", {
            htmlLength: htmlContent.length,
            plainTextLength: text.length,
          });
        }
      };

      // Attach listener, execute copy command, then remove listener
      document.addEventListener("copy", copyListener);
      const executed = document.execCommand("copy");
      document.removeEventListener("copy", copyListener);

      if (executed && copySuccess) {
        log.info("Text copied successfully with HTML format", {
          htmlLength: htmlContent.length,
          plainTextLength: text.length,
          method: "htmlFormat",
        });
        return true;
      } else {
        log.warn("HTML format copy command returned false", {
          executed,
          copySuccess,
          textLength: text.length,
        });
        return false;
      }
    } catch (error) {
      log.error("HTML format copy failed with exception", {
        error: error instanceof Error ? error.message : String(error),
        textLength: text.length,
      });
      return false;
    }
  };

  /**
   * Fallback copy method using document.execCommand with plain text
   * This method works by creating a temporary DOM element, selecting its content, and copying
   */
  const copyWithExecCommand = (text: string): boolean => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement("textarea");

      // Configure the textarea to be invisible but functional
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "-9999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      textArea.tabIndex = -1;
      textArea.setAttribute("readonly", "");

      // Add to DOM
      document.body.appendChild(textArea);

      // Focus and select the text
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      // Attempt the copy operation
      const successful = document.execCommand("copy");

      // Clean up
      document.body.removeChild(textArea);

      if (successful) {
        log.info("Text copied successfully using execCommand", {
          textLength: text.length,
          method: "execCommand",
        });
        return true;
      } else {
        log.warn("execCommand copy returned false", {
          textLength: text.length,
        });
        return false;
      }
    } catch (error) {
      log.error("execCommand copy failed with exception", {
        error: error instanceof Error ? error.message : String(error),
        textLength: text.length,
      });
      return false;
    }
  };

  /**
   * Fallback method using modern clipboard API (for environments where it's available)
   * This is kept as a secondary option for contexts where it might work
   */
  const copyWithClipboardAPI = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        log.info("Text copied successfully using Clipboard API", {
          textLength: text.length,
          method: "clipboardAPI",
        });
        return true;
      }
      return false;
    } catch (error) {
      log.warn("Clipboard API copy failed", {
        error: error instanceof Error ? error.message : String(error),
        textLength: text.length,
      });
      return false;
    }
  };

  /**
   * Manual selection method as a last resort
   * This creates a visible, temporary element that users can manually copy from
   */
  const createManualCopyFallback = (text: string): void => {
    log.info("Showing manual copy fallback", {
      textLength: text.length,
    });

    // Create a modal-like overlay
    const overlay = document.createElement("div");
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Segoe UI', sans-serif;
    `;

    // Create the content container
    const container = document.createElement("div");
    container.style.cssText = `
      background: white;
      padding: 20px;
      border-radius: 8px;
      max-width: 500px;
      max-height: 400px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    // Add instructions
    const instructions = document.createElement("p");
    instructions.textContent =
      "Please select the text below and copy it manually (Ctrl+C or Cmd+C):";
    instructions.style.cssText = `
      margin: 0 0 10px 0;
      font-size: 14px;
      color: #333;
    `;

    // Create text area with the content
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.cssText = `
      width: 100%;
      height: 200px;
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 8px;
      font-size: 12px;
      font-family: monospace;
      resize: vertical;
    `;
    textArea.readOnly = true;

    // Select all text in the textarea
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    // Close button
    const closeButton = document.createElement("button");
    closeButton.textContent = "Close";
    closeButton.style.cssText = `
      margin-top: 10px;
      padding: 8px 16px;
      background: #0078d4;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `;

    // Assembly and event handling
    container.appendChild(instructions);
    container.appendChild(textArea);
    container.appendChild(closeButton);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    // Close functionality
    const closeModal = () => {
      document.body.removeChild(overlay);
    };

    closeButton.addEventListener("click", closeModal);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeModal();
    });

    // Focus the textarea
    setTimeout(() => textArea.focus(), 100);
  };

  /**
   * Main copy handler with comprehensive fallback chain
   * This tries multiple methods in order of reliability for Office Add-ins
   */
  const handleCopy = async () => {
    if (!textToCopy || textToCopy.trim().length === 0) {
      log.warn("Attempted to copy empty text");
      setCopyState("error");
      return;
    }

    // Filter out metadata sections (Prompt Coach, Assumptions Summary) before copying
    const filteredContent = stripMetadataSections(textToCopy);
    const wasFiltered = filteredContent !== textToCopy;

    log.info("Starting copy operation", {
      textLength: textToCopy.length,
      filteredLength: filteredContent.length,
      metadataStripped: wasFiltered,
      textPreview: filteredContent.substring(0, 50) + (filteredContent.length > 50 ? "..." : ""),
    });

    // Method 1: Try HTML format copy first (provides rich text for Word Desktop)
    const htmlFormatSuccess = await copyWithHtmlFormat(filteredContent);
    if (htmlFormatSuccess) {
      setCopyState("success");
      return;
    }

    // Log warning when falling back from HTML format to plain text
    log.warn("HTML format copy failed, falling back to plain text method", {
      textLength: filteredContent.length,
      textPreview: filteredContent.substring(0, 50) + (filteredContent.length > 50 ? "..." : ""),
    });

    // Method 2: Try plain text execCommand as fallback
    const execCommandSuccess = copyWithExecCommand(filteredContent);
    if (execCommandSuccess) {
      setCopyState("success");
      return;
    }

    // Method 3: Try modern clipboard API as fallback
    const clipboardAPISuccess = await copyWithClipboardAPI(filteredContent);
    if (clipboardAPISuccess) {
      setCopyState("success");
      return;
    }

    // Method 4: Show manual copy interface as last resort
    log.warn("All automatic copy methods failed, showing manual copy interface");
    createManualCopyFallback(filteredContent);

    // Don't set error state since we're providing a manual solution
    // The user can still copy the text, just manually
  };

  const renderIcon = () => {
    switch (copyState) {
      case "success":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "error":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="9"
              y="9"
              width="13"
              height="13"
              rx="2"
              ry="2"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        );
    }
  };

  const getButtonColor = () => {
    switch (copyState) {
      case "success":
        return "#00aa00";
      case "error":
        return "#cc0000";
      default:
        return isHovered ? "#0066cc" : "#666";
    }
  };

  const getTooltipText = () => {
    switch (copyState) {
      case "success":
        return "Copied!";
      case "error":
        return "Copy failed";
      default:
        return "Copy message";
    }
  };

  return (
    <button
      className={className}
      onClick={handleCopy}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={copyState !== "idle"}
      title={getTooltipText()}
      aria-label={getTooltipText()}
      style={{
        backgroundColor: "transparent",
        border: "none",
        borderRadius: "4px",
        padding: "6px",
        cursor: copyState === "idle" ? "pointer" : "default",
        color: getButtonColor(),
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: copyState !== "idle" ? 0.7 : 1,
        ...(isHovered && copyState === "idle"
          ? {
              backgroundColor: "#f0f0f0",
            }
          : {}),
      }}
    >
      {renderIcon()}
    </button>
  );
};

export default CopyButton;
